import {
  Component,
  Host,
  h,
  Element,
  Listen,
  State,
  Prop,
} from "@stencil/core";
import * as d3 from "d3";
import { ValueFn } from "d3";
import dayjs from "dayjs";
import {
  ILiftStatusSensorResponse,
  ILiftStatusUpdated,
  ILiftStatusValues,
  ILiftStatusWebComponentError,
  ITimePoint,
} from "../../utils/types";
import {
  cloneDeep,
  debounce,
  filter,
  first,
  isEmpty,
  isObject,
  keys,
  last,
  sortBy,
  values,
} from "lodash-es";
import {
  applyErrorMessage,
  clearErrorMessage,
} from "../../utils/error-message";
import { getStore, ILiftStatusStore } from "../../store";
import { watchStoreLocale } from "../../utils/locale-state";
import { getStoreUnavailableError } from "../../utils/store-error";
import { getLocale } from "../../utils/i18n";
import { translations } from "../../utils/translations";

@Component({
  tag: "ls-recent-stops",
  styleUrl: "ls-recent-stops.css",
  shadow: true,
})
export class LsRecentStops {
  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  private store: ILiftStatusStore | null = null;
  private dateTimeFormatter: Intl.DateTimeFormat = this.createDateTimeFormatter();

  // Set the constants
  private readonly DEBOUNCEDELAY = 350;
  private readonly MARGIN = { BOTTOM: 20, LEFT: 20, RIGHT: 10, TOP: 20 };
  // key 501 is current (open doors)
  // key 515 is current (regardless of door state)
  // key 514 is last passed
  // These are the only statuses we want to show
  private readonly SENSOR_IDS = [501, 514, 515];
  private readonly focusCircleId = "focusCircle";
  private readonly focusLineXId = "focusLineX";
  private readonly focusLineYId = "focusLineY";
  private readonly focusTextContainerId = "focusTextContainer";
  private readonly focusTextId = "focusText";

  // Width will be set as soon as the component is loaded
  private width = 0;

  /**
   * The Host element
   */
  @Element() element: HTMLLsRecentStopsElement;

  // Listen for resize events and recalculate
  @Listen("resize", { target: "window" })
  redrawChartWhenWindowResizes() {
    // If the component is in loading or error state, we don't want to trigger a render of the graph
    if (this.errorState.hasError || this.isLoading) return;

    this.redrawChartWithDebounce();
  }

  // Using a debounce here to save resources
  private redrawChartWithDebounce = debounce(
    () => {
      // Redraw the chart
      this.drawChart();
      this.updateDataRange();
    },
    this.DEBOUNCEDELAY,
    // Enables the first trigger i.e. for when the screen gets maximized or media queries are triggered
    { leading: true },
  );

  private markUserScrolled = () => {
    // Mark that the user has manually scrolled
    this.hasUserScrolled = true;
  };

  private yAxisCanvas: HTMLElement;
  private chartCanvas: HTMLElement;
  private scrollContainer: HTMLElement;
  private containerElement: HTMLElement;
  private currentXScale: d3.ScaleTime<number, number> = undefined;
  private currentYScale: d3.ScaleLinear<number, number> = undefined;
  private graph: d3.Selection<SVGGElement, unknown, HTMLElement, unknown> =
    undefined;
  private yAxisSvg:
    | d3.Selection<SVGGElement, unknown, HTMLElement, unknown>
    | undefined = undefined;
  private historicalPointsResponse: ILiftStatusSensorResponse[];
  private timePoints: ITimePoint[] = [];
  private maxFloors = 0;
  private readonly MIN_CHART_WIDTH = 800;
  private timer;
  private isComponentMounted = false;
  private hasUserScrolled = false;
  private isInitialLoad = true;
  private storeUnsubscribes: Array<() => void> = [];
  private xAxis;
  private yAxis;

  /**
   * Centralized error state for tracking component errors
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: "",
  };

  /**
   * Component loading state
   */
  @State() isLoading = true;

  private appendSensorUpdatesToTimePoints = (
    data: ILiftStatusValues,
    timestamp: ILiftStatusUpdated,
  ) => {
    if (isEmpty(data)) return;

    this.updateErrorState();

    for (let i = 0; i < keys(data).length; i++) {
      const key = parseInt(keys(data)[i], 10);
      const value = parseInt(values(data)[i], 10);

      if (key === 591) {
        this.maxFloors = (value || 0) + 1;
      }

      if (!Number.isNaN(value) && this.SENSOR_IDS.includes(key)) {
        const momentTimestamp = dayjs(timestamp[key]);
        const newTimePoint: ITimePoint = {
          x: momentTimestamp.toDate(),
          y: value + 1,
          id: `${key}-${timestamp[key]}`,
          sensor: key,
          fake: false,
        };

        const updatedTimePoints = [...this.timePoints, newTimePoint];
        this.timePoints = updatedTimePoints;
      }
    }
  };

  private createDateTimeFormatter(localeInput?: string): Intl.DateTimeFormat {
    const locale = getLocale(localeInput);
    return new Intl.DateTimeFormat(translations[locale].intlLocale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  private formatTimestamp = (timestamp: dayjs.ConfigType) => {
    return this.dateTimeFormatter.format(dayjs(timestamp).toDate());
  };

  /**
   * Updates the error state based on current component conditions
   */
  private updateErrorState() {
    const previousErrorState = this.errorState.hasError;

    if (this.store === null) {
      this.errorState = getStoreUnavailableError(this.store?.state.locale);
      return;
    }

    if (this.store.state.error.hasError) {
      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };

      if (this.containerElement !== undefined) {
        // Keep the chart mounted and overlay the shared error UI on top.
        applyErrorMessage(
          this.containerElement,
          this.getErrorMessageForDisplay(),
        );
      }

      this.clearTimer();
      return;
    }

    // No error detected
    this.errorState = {
      hasError: false,
      messageKey: undefined,
      type: "",
    };

    if (this.containerElement !== undefined) {
      clearErrorMessage(this.containerElement);
    }

    // If we just recovered from an error state, restart the timer
    if (
      previousErrorState &&
      !this.errorState.hasError &&
      this.timer === null
    ) {
      this.startTimer();
    }
  }

  private startTimer() {
    // Clear any existing timer first
    this.clearTimer();

    // Initiate the timer that updates the graph
    this.timer = window.setInterval(() => {
      if (!this.isComponentMounted) {
        this.clearTimer();
        return;
      }

      this.updateDataRange();
    }, 2000);
  }

  private clearTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  private clearChart = () => {
    // Clear out the html of the chart so that we have a clear chart
    d3.select(this.yAxisCanvas).html("");
    d3.select(this.chartCanvas).html("");
  };

  private drawChart = async () => {
    // lets first remove the current chart before we draw a new one to prevent overlapping
    this.clearChart();

    // (Re)calculate width and height
    const componentWidth =
      this.element.clientWidth <= 0 ? 1 : this.element.clientWidth;

    // Use minimum chart width for better scrollable experience
    const contentWidth = Math.max(componentWidth, this.MIN_CHART_WIDTH);
    this.width = contentWidth - this.MARGIN.LEFT - this.MARGIN.RIGHT;

    const chartWidth = this.width + this.MARGIN.RIGHT;
    const yAxisWidth = this.MARGIN.LEFT;

    const height =
      componentWidth >= 800
        ? this.width / 4 - this.MARGIN.BOTTOM - this.MARGIN.TOP
        : this.width / 3 - this.MARGIN.BOTTOM - this.MARGIN.TOP;

    const chartHeight = height + this.MARGIN.BOTTOM + this.MARGIN.TOP;

    this.currentXScale = d3.scaleTime().range([0, this.width]);
    this.currentYScale = d3.scaleLinear().range([height, 0]);

    this.xAxis = d3
      .axisBottom(this.currentXScale)
      .ticks(d3.timeMinute.every(5))
      .tickFormat(d3.timeFormat("%H:%M"));
    this.yAxis = d3.axisLeft(this.currentYScale).tickFormat(d3.format("d"));

    // Create separate SVG for the y-axis (fixed on left)
    this.yAxisSvg = d3
      .select(this.yAxisCanvas)
      .append("svg")
      .attr("viewBox", `0 0 ${yAxisWidth} ${chartHeight}`)
      .attr("width", yAxisWidth)
      .attr("height", chartHeight)
      .append("g")
      .attr("transform", `translate(${yAxisWidth}, ${this.MARGIN.TOP})`);

    // add the y axis to the fixed svg
    this.yAxisSvg.append("g").attr("class", "axis y_axis").call(this.yAxis);

    // Create SVG for the scrollable chart area
    const svg = d3
      .select(this.chartCanvas)
      .append("svg")
      .attr("viewBox", `0 0 ${chartWidth} ${chartHeight}`)
      .attr("width", chartWidth)
      .attr("height", chartHeight)
      .append("g")
      .attr("transform", `translate(0, ${this.MARGIN.TOP})`);

    // add the x axis
    svg
      .append("g")
      .attr("class", "axis x_axis")
      .attr("transform", `translate(0,${height})`)
      .call(this.xAxis);

    const dataSets: ILiftStatusSensorResponse[] = !isEmpty(
      this.historicalPointsResponse,
    )
      ? this.historicalPointsResponse
      : [];

    // create timePoints
    const historicalTimePoints = [];
    for (const dataSet of dataSets) {
      const sensorId = parseInt(dataSet.sensor.id, 10);

      if (sensorId === 591) {
        this.maxFloors = (parseInt(last(dataSet.rows)?.val, 10) || 0) + 1;
      }

      for (const row of dataSet.rows) {
        const value = parseInt(row.val, 10);

        if (!Number.isNaN(value) && this.SENSOR_IDS.includes(sensorId)) {
          const newTimePoint: ITimePoint = {
            x: dayjs(row.ts).toDate(),
            y: value + 1,
            id: `${sensorId}-${row.ts}`,
            sensor: sensorId,
            fake: false,
          };

          historicalTimePoints.push(newTimePoint);
        }
      }
    }

    // merge these time points with the ones we already have
    const mergedTimePoints: ITimePoint[] = [
      ...historicalTimePoints,
      ...this.timePoints,
    ];

    // sort the time points on date
    mergedTimePoints.sort((timePointA, timePointB) =>
      dayjs(timePointA.x).isBefore(dayjs(timePointB.x)) ? -1 : 1,
    );

    // set the merged time points
    this.timePoints = mergedTimePoints;

    // create a graph element
    this.graph = svg.append("g");

    // create an element for onFocus
    const focus = svg.append("g").style("display", "none");

    // append the x axis line to the onFocus element
    focus
      .append("line")
      .attr("id", this.focusLineXId)
      .attr("class", "focusLine");

    // append the y axis line to the onFocus element
    focus
      .append("line")
      .attr("id", this.focusLineYId)
      .attr("class", "focusLine");

    // append a circle to the onFocus element with a radius of 5px
    focus
      .append("circle")
      .attr("id", this.focusCircleId)
      .attr("r", 5)
      .attr("class", "circle focusCircle");

    // add a text container to the onFocus element
    const textContainer = focus
      .append("g")
      .attr("id", this.focusTextContainerId)
      .attr("class", "focus_text_container");

    // create the outer box where the text will appear in
    textContainer
      .append("rect")
      .attr("width", 180)
      .attr("height", 20)
      .attr("x", -90)
      .attr("y", -20)
      .attr("rx", 5)
      .attr("ry", 5);

    // create the inner box where the text will appear in
    textContainer
      .append("text")
      .attr("y", -10)
      .attr("id", this.focusTextId)
      .attr("text-anchor", "middle")
      .attr("dy", ".35em");

    // use the return value here as we need it later for the overlay
    const [floorExtend, datesExtend] = this.updateDataRange();

    // create a bisector object to find the current data item on move move on the overlay
    const bisectDate = d3.bisector((timepoint: ITimePoint) => timepoint.x).left;

    // create an overlay which will show the focus items on the correct point
    svg
      .append("rect")
      .attr("class", "overlay")
      .attr("width", this.width)
      .attr("height", height)
      .on("mouseout", () => focus.style("display", "none"))
      .on("mousemove", (event) => {
        const mouse = d3.pointer(event);
        const mouseDate = this.currentXScale.invert(mouse[0]);
        // we need to sort the time points as they might not be yet. and after that find the index in the sorted time points
        const clonedTimePoints: ITimePoint[] = cloneDeep(this.timePoints);
        clonedTimePoints.sort((a, b) => a.x.valueOf() - b.x.valueOf());
        const i = bisectDate(clonedTimePoints, mouseDate); // returns the index to the current data item

        // get the current data item and the one before that so we can see which one is closes to the current mouse position
        const timePoint0 = clonedTimePoints[i - 1];
        const timePoint1 = clonedTimePoints[i];

        if (isObject(timePoint0) && isObject(timePoint1)) {
          // work out which date value is closest to the mouse
          const closestTimePoint =
            mouseDate.getTime() - timePoint0.x.getTime() >
            timePoint1.x.getTime() - mouseDate.getTime()
              ? timePoint1
              : timePoint0;

          // get the x and y values of that closest point
          const x = this.currentXScale(closestTimePoint.x);
          const y = this.currentYScale(closestTimePoint.y);

          focus.select(`#${this.focusCircleId}`).attr("cx", x).attr("cy", y);
          focus
            .select(`#${this.focusLineXId}`)
            .attr("x1", x)
            .attr("y1", this.currentYScale(floorExtend[0]))
            .attr("x2", x)
            .attr("y2", this.currentYScale(floorExtend[1]));
          focus
            .select(`#${this.focusLineYId}`)
            .attr("x1", this.currentXScale(datesExtend[0]))
            .attr("y1", y)
            .attr("x2", this.currentXScale(datesExtend[1]))
            .attr("y2", y);
          focus
            .select(`#${this.focusTextContainerId}`)
            .attr("transform", `translate(${x + 2},${y})`)
            .select(`#${this.focusTextId}`)
            .text(this.formatTimestamp(closestTimePoint.x));

          focus.style("display", null);
        }
      });
  };

  private updateDataRange = () => {
    const now = dayjs();
    const start = this.getSelectedStartPeriod();

    // set last updated to the current moment
    // filter out time points older then the start selected, filter out fake values, and any points too close to 501 points
    let filteredTimePoints = filter(
      this.timePoints,
      (timePoint: ITimePoint) => {
        const isAfterStart = dayjs(timePoint.x).isAfter(start);
        const isNotFake = !timePoint.fake;

        return isAfterStart && isNotFake;
      },
    );

    // Add a time point for the last known stop, so that graph does not display an empty state
    if (filteredTimePoints.length == 0) {
      for (const point of this.timePoints) {
        if (point.sensor == 515) {
          const currentFloorPoint = cloneDeep(point);
          currentFloorPoint.x = now.toDate();
          currentFloorPoint.id = `${currentFloorPoint.y}-${now.toDate()}`;
          currentFloorPoint.fake = true;

          filteredTimePoints = [...filteredTimePoints, currentFloorPoint];
        }
      }
    }

    // clone the last point
    const clonedTimePoints: ITimePoint[] = cloneDeep(filteredTimePoints);
    clonedTimePoints.sort((a, b) => a.x.valueOf() - b.x.valueOf());
    const fakeValueLast = cloneDeep(last(clonedTimePoints));

    // add a fake point (to the end) if we were successful on cloning the last point
    // We do this to add a leading line
    if (fakeValueLast !== undefined) {
      fakeValueLast.x = now.toDate();
      fakeValueLast.id = `${fakeValueLast.y}-${now.toDate()}`;
      fakeValueLast.fake = true;

      const timePointsWithFakeValue = [...filteredTimePoints, fakeValueLast];
      this.timePoints = timePointsWithFakeValue;
    }

    // clone the last point again
    const fakeValueFirst = cloneDeep(first(clonedTimePoints));

    // add a fake point (to the start) if we were successful on cloning the last point
    // We do this to add a trailing line
    if (fakeValueFirst !== undefined) {
      fakeValueFirst.x = now.subtract(1, "hour").toDate();
      fakeValueFirst.id = `${fakeValueFirst.y}-${now
        .subtract(1, "hour")
        .toDate()}`;
      fakeValueFirst.fake = true;

      const timePointsWithFakeValue = [...this.timePoints, fakeValueFirst];
      this.timePoints = timePointsWithFakeValue;
    }

    const floorExtent = [1, this.maxFloors];

    const datesExtend = d3.extent(this.timePoints, (timePoint) => timePoint.x);
    datesExtend[1] = now.toDate();

    this.currentXScale.domain([start.valueOf(), now.valueOf()]);
    this.currentYScale.domain(floorExtent);

    // Remove previous lines
    if (this.graph !== null) {
      this.graph?.selectAll(".line").remove();

      for (let i = 1; i <= this.maxFloors; i++) {
        this.graph
          ?.append("line")
          .attr("y1", this.currentYScale(i))
          .attr("y2", this.currentYScale(i))
          .attr("x1", 0.5)
          .attr("x2", this.width)
          .attr("class", "line");
      }

      const yTicks = this.maxFloors > 0 ? this.maxFloors : 1;
      this.yAxis.ticks(yTicks);

      // Update y-axis in fixed svg
      if (this.yAxisSvg !== undefined) {
        d3.select(this.element.shadowRoot.querySelector(".y_axis")).call(
          this.yAxis,
        );
      }

      // Remove all previous paths
      this.graph?.selectAll(".path").remove();

      this.graph
        .append("path")
        .datum(
          sortBy(this.timePoints, (t) => t.x.getTime()).map((timePoint) => [
            timePoint.x.getTime(),
            timePoint.y,
          ]),
        )
        .attr("fill", "none")
        .attr("class", "path")
        .attr(
          "d",
          d3
            .line()
            .x((d) => this.currentXScale(d[0]))
            .y((d) => this.currentYScale(d[1])) as ValueFn<
            SVGPathElement,
            number[][],
            string | number | boolean
          >,
        );

      // Remove previous circles
      this.graph.selectAll(`.point`).remove();

      // append new circles for the new time points
      this.graph
        .selectAll(".circle")
        .data(sortBy(this.timePoints, "sensor").reverse() as ITimePoint[])
        .enter()
        .append("circle")
        .attr("cx", (timePoint) => this.currentXScale(timePoint.x))
        .attr("cy", (timePoint) => this.currentYScale(timePoint.y))
        .attr("r", 5)
        .attr("class", (timePoint) => {
          let result = "stop";

          const sensorType = parseInt(`${timePoint.sensor}`, 10);

          if (sensorType === 514 || sensorType === 515) {
            result = "passed";
          }

          if (timePoint.fake) {
            result = "fake";
          }

          return `${result} point`;
        });

      // update the axis
      d3.select(this.element.shadowRoot.querySelector(".x_axis")).call(
        this.xAxis,
      );
      d3.select(this.element.shadowRoot.querySelector(".y_axis")).call(
        this.yAxis,
      );

      // Scroll to the right to show most recent data only on initial load
      if (
        this.scrollContainer !== undefined &&
        this.isInitialLoad &&
        !this.hasUserScrolled
      ) {
        setTimeout(() => {
          this.scrollContainer.scrollLeft = this.scrollContainer.scrollWidth;
          this.isInitialLoad = false;
        }, 0);
      }

      return [floorExtent, datesExtend];
    }
  };

  private getSelectedStartPeriod = () => {
    // Default time
    const startTime = dayjs().subtract(1, "hour");

    return startTime;
  };

  private getErrorMessageForDisplay(): string {
    if (this.store === null) return "";
    const errorKey = this.errorState.messageKey ?? "CONSOLE_INFO";
    return this.store.t(`errors:${errorKey}`);
  }

  private updateErrorMessageElement() {
    if (this.containerElement === undefined || this.store === null) return;

    if (!this.errorState.hasError) {
      clearErrorMessage(this.containerElement);
      return;
    }

    applyErrorMessage(this.containerElement, this.getErrorMessageForDisplay());
  }

  async componentWillLoad() {
    this.isComponentMounted = true;
    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store, (locale) => {
      this.dateTimeFormatter = this.createDateTimeFormatter(locale);
      this.updateErrorMessageElement();
    });
    this.updateErrorState();

    // Subscribe to error changes before first render
    if (this.store !== null) {
      this.storeUnsubscribes.push(
        this.store.onChange("error", () => {
          this.updateErrorState();
          this.updateErrorMessageElement();
        }),
      );

    }
  }

  async componentDidLoad() {
    if (this.store === null) {
      this.isLoading = false;
      this.updateErrorState();
      return;
    }

    this.historicalPointsResponse = this.store.state.historicalSensorData;
    await this.drawChart();
    this.updateDataRange();
    this.isLoading = false;

    this.updateErrorState();

    // Attach scroll event listener
    if (this.scrollContainer !== undefined) {
      this.scrollContainer.addEventListener("scroll", this.markUserScrolled);
    }

    // Store subscriptions for cleanup
    this.storeUnsubscribes.push(
      this.store.onChange("historicalSensorData", (sensorValues) => {
        this.historicalPointsResponse = sensorValues;
        this.drawChart();
      }),
    );

    this.storeUnsubscribes.push(
      this.store.onChange("currentSensorData", (sensorData) => {
        const values = sensorData.values;
        const timestamps = sensorData.updated;

        this.appendSensorUpdatesToTimePoints(values, timestamps);
        this.updateDataRange();
      }),
    );

    this.startTimer();
  }

  render() {
    return (
      <Host>
        <div
          class={{ container: true, "has-error": this.errorState.hasError }}
          ref={(el) => {
            this.containerElement = el;
          }}
        >
          <div class="chart-container">
            <div
              class="y-axis-container"
              ref={(el) => {
                this.yAxisCanvas = el;
              }}
            ></div>
            <div
              class="chart-scroll-container"
              ref={(el) => {
                this.scrollContainer = el;
              }}
            >
              <div
                class="chart"
                ref={(el) => {
                  this.chartCanvas = el;
                }}
              ></div>
            </div>
          </div>
          {this.isLoading && (
            <div class="loading">
              {this.store?.t("ui:general.loading") ?? "Loading..."}
            </div>
          )}
          <slot></slot>
        </div>
      </Host>
    );
  }

  disconnectedCallback() {
    this.isComponentMounted = false;
    this.clearTimer();

    // Remove scroll event listener
    if (this.scrollContainer !== undefined) {
      this.scrollContainer.removeEventListener(
        "scroll",
        this.markUserScrolled,
      );
    }

    // Unsubscribe from all store changes
    this.storeUnsubscribes.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.storeUnsubscribes = [];

    if (this.containerElement !== undefined) {
      clearErrorMessage(this.containerElement);
    }
  }
}
